import { __decorate } from "tslib";
import { NumberParser } from '@genesislcap/foundation-utils';
import { NumberField as FASTNumberField } from '@microsoft/fast-components';
import { DOM, attr } from '@microsoft/fast-element';
import { foundationNumberFieldStyles as styles } from './number-field.styles';
import { foundationNumberFieldTemplate as template } from './number-field.template';
const formattedInputPrecision = 12;
/**
 * @tagname %%prefix%%-number-field
 */
export class NumberField extends FASTNumberField {
    constructor() {
        super(...arguments);
        this.hideStep = false;
        this.value = '';
        this.withFormatting = false;
        this.locale = 'en-US';
        this.options = {
            maximumFractionDigits: 3,
            minimumFractionDigits: 0,
        };
    }
    autocompleteChanged() {
        if (this.proxy instanceof HTMLInputElement) {
            this.proxy.autocomplete = this.autocomplete;
        }
    }
    sanitiseInputVal(inputVal) {
        return String(new NumberParser(this.locale).parse(String(inputVal)));
    }
    toIntlNumberFormat(value) {
        return Intl.NumberFormat(this.locale, this.options).format(Number(value));
    }
    validateValue(value, withFormatting = true) {
        let validValue = parseFloat(parseFloat(withFormatting ? this.sanitiseInputVal(value) : value).toPrecision(formattedInputPrecision));
        if (isNaN(validValue)) {
            validValue = '';
        }
        return String(validValue);
    }
    validateMinMax(value, withFormatting = true) {
        var _a, _b;
        let validValue = parseFloat(this.validateValue(value, withFormatting));
        if (isNaN(validValue)) {
            validValue = '';
        }
        else {
            validValue = Math.min(validValue, (_a = this.max) !== null && _a !== void 0 ? _a : validValue);
            validValue = Math.max(validValue, (_b = this.min) !== null && _b !== void 0 ? _b : validValue);
        }
        return String(validValue);
    }
    stepUp() {
        const value = parseFloat(this.value);
        const stepUpValue = !isNaN(value)
            ? value + this.step
            : this.min > 0
                ? this.min
                : this.max < 0
                    ? this.max
                    : !this.min
                        ? this.step
                        : 0;
        this.value = stepUpValue.toString();
        this.control.value = this.validateMinMax(stepUpValue.toString(), this.withFormatting);
        this.handleTextInput();
    }
    stepDown() {
        const value = parseFloat(this.value);
        const stepDownValue = !isNaN(value)
            ? value - this.step
            : this.min > 0
                ? this.min
                : this.max < 0
                    ? this.max
                    : !this.min
                        ? 0 - this.step
                        : 0;
        this.value = stepDownValue.toString();
        this.control.value = this.validateMinMax(stepDownValue.toString(), this.withFormatting);
        this.handleTextInput();
    }
    hasSeparatorWithoutDecimals() {
        const regexString = `[.]$`;
        return new RegExp(regexString);
    }
    handleTextInput() {
        this['isUserInput'] = true;
        if (this.withFormatting && !new NumberParser(this.locale).hasSeparator(this.control.value)) {
            DOM.queueUpdate(() => {
                const rawValue = this.validateValue(this.control.value);
                this.control.value = this.toIntlNumberFormat(rawValue);
                this.value = rawValue;
                this.$emit('input');
            });
        }
        else if (!this.withFormatting &&
            !this.hasSeparatorWithoutDecimals().test(this.control.value)) {
            DOM.queueUpdate(() => {
                this.control.value = this.control.value.replace(/[^0-9\-+e.]/g, '');
                this.value = this.control.value;
                this.$emit('input');
            });
        }
    }
    valueChanged(previous, next) {
        if ((next === null || next === undefined) && this.control) {
            this.control.value = '';
            return;
        }
        if (next !== undefined && next !== null && next !== previous) {
            if (!this['isUserInput']) {
                this.value = this.withFormatting
                    ? this.validateMinMax(this.value)
                    : this.validateMinMax(this.value, false);
                DOM.queueUpdate(() => {
                    this.control.value =
                        this.withFormatting && this.value ? this.toIntlNumberFormat(this.value) : this.value;
                });
                return;
            }
            const value = next;
            DOM.queueUpdate(() => {
                var _a;
                if (((_a = this.control) === null || _a === void 0 ? void 0 : _a.value) === '') {
                    this.control.value = this.withFormatting ? this.toIntlNumberFormat(value) : value;
                    return;
                }
                if (this.value === '') {
                    this.control.value = next;
                    return;
                }
                this.control.value = this.withFormatting ? this.toIntlNumberFormat(value) : value;
                this.value = this.withFormatting
                    ? this.sanitiseInputVal(this.control.value)
                    : this.control.value;
            });
        }
    }
    handleBlur() {
        this['isUserInput'] = false;
        this.value = this.validateMinMax(this.control.value, this.withFormatting);
        this.control.value =
            this.withFormatting && this.value ? this.toIntlNumberFormat(this.value) : this.value;
        this.$emit('input');
    }
}
__decorate([
    attr
], NumberField.prototype, "autocomplete", void 0);
__decorate([
    attr({ mode: 'boolean' })
], NumberField.prototype, "hideStep", void 0);
__decorate([
    attr
], NumberField.prototype, "value", void 0);
__decorate([
    attr({ mode: 'boolean' })
], NumberField.prototype, "withFormatting", void 0);
__decorate([
    attr
], NumberField.prototype, "locale", void 0);
export const foundationNumberFieldShadowOptions = {
    delegatesFocus: true,
    mode: 'open',
};
export const defaultNumberFieldConfig = {
    stepDownGlyph: `
    <svg class="step-down-glyph" viewBox="0 0 24 24" width="1rem" height="1rem" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.1018 8C5.02785 8 4.45387 9.2649 5.16108 10.0731L10.6829 16.3838C11.3801 17.1806 12.6197 17.1806 13.3169 16.3838L18.8388 10.0731C19.5459 9.2649 18.972 8 17.898 8H6.1018Z"/>
    </svg>
  `,
    stepUpGlyph: `
    <svg class="step-up-glyph" viewBox="0 0 24 24" width="1rem" height="1rem" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.1018 16.9814C5.02785 16.9814 4.45387 15.7165 5.16108 14.9083L10.6829 8.59762C11.3801 7.80079 12.6197 7.80079 13.3169 8.59762L18.8388 14.9083C19.5459 15.7165 18.972 16.9814 17.898 16.9814H6.1018Z"/>
    </svg>
  `,
};
/**
 * The Foundation NumberField
 *
 * @public
 * @remarks
 * HTML Element: \<foundation-number-field\>
 */
export const foundationNumberField = NumberField.compose(Object.assign({ baseName: 'number-field', template,
    styles, shadowOptions: foundationNumberFieldShadowOptions }, defaultNumberFieldConfig));
